'use client';
import { getAnonUser, OPTIMIZELY_EVENT_VALUES } from './utils';
import { getOptimizelyAttributes } from './attributes';
import Script from 'next/script';
import Cookies from 'js-cookie';
import { EventEmitter } from 'events';
import { createContext, useContext, useEffect, useState } from 'react';
import { pushToDataLayer } from 'services/Gtm/functions';
import optimizelySdk, { type OptimizelyUserContext, enums, type ListenerPayload } from '@optimizely/optimizely-sdk';
import type { OptimizelyDecisionTracking, OptimizelyTrackTracking } from 'services/Gtm/entities';
import type CustomWindow from 'interfaces/Utils';
import type { PageAdConfig } from '@/app_lib/types/Ad';

declare let window: CustomWindow;

const optimizely = optimizelySdk.createInstance({
  sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_KEY,
});

const userFlagsEmitter = new EventEmitter();

export interface VariantProps {
  variant?: string | null;
  enabled?: boolean;
  variables?: any;
}

export interface OptimizelyUserInfo {
  user?: OptimizelyUserContext;
  [key: string]: VariantProps | OptimizelyUserContext | undefined;
}

interface TrackingPayload {
  userId: string;
  attributes: string;
  event: {
    id: string;
    experimentId: string;
    key: string;
    tags: {
      $opt_event_properties: {
        URL: string;
        value: number;
      };
    };
  };
  eventKey: string;
  eventTags: {
    $opt_event_properties: {
      URL: string;
      value: number;
    };
  };
  eventExperiment: string;
}

interface OnDecisionNofication {
  type: string;
  userId: string;
  attributes: string;
  decisionInfo: {
    enabled: boolean;
    flagKey: string;
    ruleKey: string;
    variationKey: string;
  };
}

const currentTests = [
  {
    location: ['2025', '2024', 'article'],
    tags: [],
    excludedTags: [],
    section: [],
    excludedSections: [],
    testName: 'socialShareV2',
  },
  {
    location: ['section'],
    testName: 'section',
  },
  {
    location: ['checkout'],
    testName: 'helio-setup',
  },
  {
    location: ['education'],
    testName: 'education',
  },
  {
    location: ['2025', '2024', 'article'],
    testName: 'article-replatform',
  },
  {
    location: ['*'],
    testName: 'googleOneTap',
  },
  {
    location: ['*'],
    testName: 'googleOneTap-mobile',
  },
  {
    location: ['*'],
    testName: 'asiaMigration',
  },
  {
    location: ['*'],
    testName: 'crownInitalHeight',
  },
  {
    location: ['article', '2025', '2024'],
    testName: 'articleStickyFooter',
  },
  {
    location: ['people'],
    testName: 'peoplePage',
  },
  {
    location: ['article', '2025', '2024'],
    testName: 'googleSourceButton',
  },
  {
    location: ['2025', '2024', 'article'],
    testName: 'desktop_right_rail',
  },
  {
    location: ['article', '2024', '2025'],
    primarySection: ['leadership', 'future-of-work', 'workplace-culture', 'c-suite', 'success'],
    excludedTags: ['always free', 'health evergreen', 'personal finance evergreen'],
    testName: 'pianoTemplateLocation',
  },
];

const canRunTest = (test, pageAdConfig) => {
  try {
    const { location, primarySection, tags, excludedTags, excludedSections } = test;
    const path = window.location.pathname;
    const locationInPath = location.some((loc) => path.includes(loc)) || location[0] === '*';
    const primarySectionInPageAdConfig = primarySection
      ? primarySection.some((sec) => pageAdConfig.ch.toLowerCase() === sec) || primarySection[0] === '*'
      : true;
    const pageAdConfigTags = pageAdConfig.tags?.map((tag) => tag.toLowerCase());

    const tagsInPageAdConfig = tags ? tags.some((tag) => pageAdConfigTags.includes(tag)) || tags[0] === '*' : true;
    const excludedTagsNotInPageAdConfig = excludedTags
      ? !excludedTags.some((tag) => pageAdConfigTags.includes(tag))
      : true;

    const excludedSectionsNotInPageAdConfig = excludedSections
      ? !excludedSections.some((sec) => pageAdConfig.ch.toLowerCase() === sec)
      : true;

    return (
      locationInPath &&
      primarySectionInPageAdConfig &&
      tagsInPageAdConfig &&
      excludedTagsNotInPageAdConfig &&
      excludedSectionsNotInPageAdConfig
    );
  } catch {
    return false;
  }
};

const OptimizelyContext = createContext<{
  userFlags: OptimizelyUserInfo;
  optimizely: typeof optimizelySdk | null;
}>({
  userFlags: {},
  optimizely: null,
});

export const useOptimizely = () => useContext(OptimizelyContext);

export const OptimizelyProvider = ({
  children,
  pageAdConfig,
}: {
  children: React.ReactNode;
  pageAdConfig?: PageAdConfig;
}) => {
  const [userFlags, setUserFlags] = useState<OptimizelyUserInfo>({});

  useEffect(() => {
    const calculateTimeOnPage = () => {
      const startTime = Date.now();
      window.addEventListener('beforeunload', async () => {
        const endTime = Date.now();
        const timeOnPage = Math.round((endTime - startTime) / 1000);
        const userId = Cookies.get('pianoUid') || getAnonUser();
        const attributes = await getOptimizelyAttributes(pageAdConfig);
        const user = optimizely?.createUserContext(userId, attributes);

        const adImpressionCount = parseInt(sessionStorage.getItem('adImpressions') || '0', 10);
        if (user) {
          // @ts-ignore
          user.trackEvent('time_on_page', { value: timeOnPage });
          // @ts-ignore
          user.trackEvent('ad_impressions', { value: adImpressionCount });
          const scrollDepth = parseInt(sessionStorage.getItem('scrollDepth') || '0', 10);
          // @ts-ignore
          user.trackEvent('scroll_depth', { value: scrollDepth });
          const pagesVisited = parseInt(sessionStorage.getItem('pagesVisited') || '0', 10);
          user.trackEvent('pages_visited', { value: pagesVisited });
          sessionStorage.removeItem('pagesVisited');
          sessionStorage.removeItem('scrollDepth');
          sessionStorage.removeItem('adImpressions');
          if (timeOnPage < 10) {
            // @ts-ignore
            user.trackEvent('bounce');
          }
        }
      });
    };

    const calculateScrollDepth = () => {
      let scrollDepth = parseInt(sessionStorage.getItem('scrollDepth') || '0', 10);
      window.addEventListener('scroll', () => {
        const scrolled = window.scrollY + window.innerHeight;
        const totalHeight = document.documentElement.scrollHeight;
        const depth = Math.round((scrolled / totalHeight) * 100);
        if (depth > scrollDepth) {
          scrollDepth = depth;
          sessionStorage.setItem('scrollDepth', scrollDepth.toString());
        }
      });
    };

    const calculatePagesVisited = async () => {
      let pagesVisited = parseInt(sessionStorage.getItem('pagesVisited') || '0', 10);
      pagesVisited += 1;
      sessionStorage.setItem('pagesVisited', pagesVisited.toString());
    };

    const addTracking = () => {
      optimizely?.notificationCenter.addNotificationListener(
        enums.NOTIFICATION_TYPES.DECISION,
        (notificationData: ListenerPayload) => {
          const { type, userId, attributes, decisionInfo } = notificationData as unknown as OnDecisionNofication;
          const trackingInfo: OptimizelyDecisionTracking = {
            attributes,
            enabled: decisionInfo.enabled,
            event: 'optimizely-decision',
            flagKey: decisionInfo.flagKey,
            ruleKey: decisionInfo.ruleKey,
            type,
            userId,
            variationKey: decisionInfo.variationKey,
          };
          window?.dbbRum?.push(['tag1', `experiment_${decisionInfo.flagKey}_${decisionInfo.variationKey}`]);
          pushToDataLayer(trackingInfo);
        },
      );

      optimizely?.notificationCenter.addNotificationListener(
        enums.NOTIFICATION_TYPES.TRACK,
        (notificationData: ListenerPayload) => {
          const { userId, attributes, eventKey, eventTags } = notificationData as unknown as TrackingPayload;
          const trackingInfo: OptimizelyTrackTracking = {
            attributes,
            event: 'optimizely-track',
            eventKey,
            userId,
            value: eventTags.$opt_event_properties.value,
          };
          const optimizelyData = window.dataLayer.filter(
            (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
          );
          optimizelyData.forEach((item) => {
            const decision = item as OptimizelyDecisionTracking;
            const path = window.location.pathname;
            const currentTest = currentTests.find((test) => test.testName === decision.flagKey);
            const locationInPath =
              currentTest?.location.some((loc) => path.includes(loc)) || currentTest?.location[0] === '*';
            if (currentTest && locationInPath) {
              trackingInfo.eventExperiment = decision.flagKey;
              trackingInfo.eventExperimentId = decision.variationKey;
              pushToDataLayer(trackingInfo);
            }
          });
        },
      );
    };

    void optimizely?.onReady().then(async ({ success }) => {
      if (!success || !optimizely) {
        return;
      }
      addTracking();
      const userId = getAnonUser();
      const attributes = await getOptimizelyAttributes(pageAdConfig);
      const user = optimizely.createUserContext(userId, attributes);
      if (!user) {
        return;
      }

      const newUserFlags: OptimizelyUserInfo = { user };

      for (let i = 0; i !== currentTests.length; i += 1) {
        const { testName } = currentTests[i];

        const canRun = canRunTest(currentTests[i], pageAdConfig);

        if (!canRun) {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
          continue;
        }
        try {
          const decision = user.decide(testName);
          const urlParams = new URLSearchParams(window.location.search);
          const testOverride = urlParams.get(`${testName}`);
          const testOverrideBoolean = testOverride ? testOverride.toLowerCase() !== 'off' : decision.enabled;
          const testOverrideVariant = testOverride ? testOverride.toLowerCase() : decision.variationKey;
          newUserFlags[testName] = {
            enabled: testOverrideBoolean,
            variables: decision.variables,
            variant: testOverrideVariant,
          };
        } catch {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
        }
      }
      setUserFlags(newUserFlags);
      userFlagsEmitter.emit('userFlagsUpdated', newUserFlags);
    });

    calculateTimeOnPage();
    calculateScrollDepth();
    calculatePagesVisited();
  }, [pageAdConfig]);

  // @ts-ignore
  return <OptimizelyContext.Provider value={{ userFlags, optimizely }}>{children}</OptimizelyContext.Provider>;
};

const incrementAdImpressions = () => {
  let adImpressions = parseInt(sessionStorage.getItem('adImpressions') || '1', 10);
  adImpressions += 1;
  sessionStorage.setItem('adImpressions', adImpressions.toString());
};

export const trackOptimizelyEvent = async (event: string, properties: Record<string, any>) => {
  const userId = getAnonUser();
  const attributes = await getOptimizelyAttributes(window?.arcSpanPageConfig);
  const user = optimizely?.createUserContext(userId, attributes);
  if (user) {
    const tags = {
      value: properties.value || 0,
      $opt_event_properties: properties,
    };
    // @ts-ignore
    user.trackEvent(event, tags);
  }
};

export const useIntersectionTracking = (elementId: string, properties: Record<string, any>, event: string) => {
  useEffect(() => {
    const targetElement = document.getElementById(elementId);
    if (!targetElement) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            const userId = getAnonUser();
            const attributes = await getOptimizelyAttributes(window?.arcSpanPageConfig);
            const user = optimizely?.createUserContext(userId, attributes);
            if (event.includes('ad')) incrementAdImpressions();
            if (user) {
              const tags = {
                value: properties.value || 0,
                $opt_event_properties: properties,
              };

              // @ts-ignore
              user.trackEvent(event, tags);
            }
          }
        });
      },
      { threshold: 1.0 }, // Fires when the entire element is in view
    );

    observer.observe(targetElement);

    return () => observer.disconnect();
  }, [elementId, properties]);
};

export const OptimizelyScript: React.FC = () => (
  <Script
    src={process.env.NEXT_PUBLIC_OPTIMIZELY_URL}
    id='optimizely'
    async
    strategy='beforeInteractive'
  />
);

export const NextOptimzely = () => {
  useIntersectionTracking(
    'edu_article_instream_ad_0',
    { value: OPTIMIZELY_EVENT_VALUES.defaultAdImpression },
    'saw_first_ad',
  );
  useIntersectionTracking(
    'edu_article_instream_ad_1',
    { value: OPTIMIZELY_EVENT_VALUES.defaultAdImpression },
    'saw_second_ad',
  );

  return <></>;
};

export { userFlagsEmitter };
